/**
 * Which implementation of `DecisionTreeApi` the app uses.
 *
 * One place, so swapping the mock for the generated client is one edit here rather than a
 * search through the pages. The real one cannot be written yet: the nine operations are in
 * `SalesRecommendationHubModel` but not in `@amzn/salesrecommendationhub-client`, because the
 * model change has not been published to a version set.
 *
 * When it is, this becomes:
 *
 *   import { getCpaClient } from './CpaClient';
 *   import { PutDecisionTreeDraftCommand, ... } from '@amzn/salesrecommendationhub-client';
 *   class RealDecisionTreeApi implements DecisionTreeApi { ... }
 *
 * and the adapter is thin because `DecisionTreeApi`'s shapes were written to match the model.
 */
import type { DecisionTreeApi } from './treeApiTypes';
import { DualWriteDecisionTreeApi } from './DualWriteDecisionTreeApi';
import { MockDecisionTreeApi, installMockHooks } from './MockDecisionTreeApi';
import { RealDecisionTreeApi } from './RealDecisionTreeApi';

let api: DecisionTreeApi | null = null;

export function getDecisionTreeApi(treeId: string): DecisionTreeApi {
  if (!api) {
    const mock = new MockDecisionTreeApi();
    installMockHooks(treeId);

    /*
     * `?tree=shadow` adds the real service to the WRITE path only.
     *
     * Off by default, and that default is the point: the service's storage is an in-memory map
     * that a cold start empties and two instances do not share, so making it the source of truth
     * would lose the tree at random. Shadowing the writes answers the question that is actually
     * open -- do they authenticate, does the payload map, do the conditional writes behave --
     * while the editor keeps reading the mock and the demo keeps working.
     *
     * A URL flag rather than a config value, because this is a diagnostic and not a stage.
     */
    const shadow =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('tree') === 'shadow';

    const banner = (bg: string, text: string) =>
      // eslint-disable-next-line no-console
      console.info(
        `%c[CPA]%c ${text}`,
        `background:${bg};color:#fff;padding:1px 6px;border-radius:3px;font-weight:600`,
        'color:#5f6b7a',
      );

    if (shadow) {
      api = new DualWriteDecisionTreeApi(mock, new RealDecisionTreeApi());
      banner(
        '#0073bb',
        '决策树：读 MOCK，写同时影子发给真服务 —— UI 用的始终是 mock 的结果，影子写失败只打日志。' +
          '注意服务端存储是内存 Map、冷启动即清空，所以这验证的是「请求能通 / 字段对得上」，不是「数据存下来了」',
      );
    } else {
      api = mock;
      banner(
        '#8a6116',
        '决策树用的是 MOCK API —— 状态在 localStorage。加 ?tree=shadow 让写操作同时发给真服务。' +
          '控制台可用：__cpaMock.otherEditorOpens() / otherEditorCommits() / dump() / reset()',
      );
    }
  }
  return api;
}

/** For tests, and for the switch to the real client. */
export function setDecisionTreeApi(next: DecisionTreeApi | null): void {
  api = next;
}
